import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';

/**
 * Review Required dialog: shows the safe scanner finding summary for one
 * reviewable document version and lets an authorized reviewer Approve
 * (creates a new RAG ingestion attempt for the SAME document version, no
 * re-upload) or Reject (leaves the document unavailable to RAG/query).
 *
 * Deliberately small and focused -- not a document-review application. Only
 * ever displays the SAFE fields the backend already returned (label,
 * severity, page number, a bounded/redacted context snippet where the
 * backend chose to include one) -- this component never has access to, and
 * therefore can never accidentally render, a matched secret/PII value.
 *
 * review is null while loading/on error; once loaded its shape is exactly
 * DocumentReviewController's own safe payload (id, findings, reviewStatus,
 * reviewerUserId, decisionAt, reviewerNote, createdAt).
 */
const DocumentReviewDialog = ({
  open,
  documentTitle,
  loading,
  error,
  review,
  approving,
  rejecting,
  onApprove,
  onReject,
  onClose,
}) => {
  const [note, setNote] = useState('');

  const resetAndClose = () => {
    setNote('');
    onClose();
  };

  const busy = approving || rejecting;
  const isPending = review?.reviewStatus === 'pending';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : resetAndClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Review Required</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          {documentTitle && (
            <Typography variant="body2" color="text.secondary">
              Document: {documentTitle}
            </Typography>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {!loading && error && <Alert severity="error">{error}</Alert>}

          {!loading && !error && review && (
            <>
              {!isPending && (
                <Alert
                  severity={
                    review.reviewStatus === 'approved' ? 'success' : 'info'
                  }
                >
                  {review.reviewStatus === 'approved'
                    ? 'This review has already been approved.'
                    : 'This review has already been rejected.'}
                </Alert>
              )}

              <Typography variant="subtitle2">Scanner findings</Typography>
              {review.findings.map((finding, index) => (
                <Box key={index}>
                  {index > 0 && <Divider sx={{ my: 1 }} />}
                  <Typography variant="body2">
                    <strong>{finding.label}</strong>
                    {finding.page_number
                      ? ` — Page ${finding.page_number}`
                      : ''}
                  </Typography>
                  {finding.safe_context && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: 'italic', mt: 0.5 }}
                    >
                      &ldquo;{finding.safe_context}&rdquo;
                    </Typography>
                  )}
                </Box>
              ))}

              {isPending && (
                <TextField
                  label="Reviewer note (optional)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  disabled={busy}
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                />
              )}

              {review.reviewerNote && !isPending && (
                <Typography variant="body2" color="text.secondary">
                  Note: {review.reviewerNote}
                </Typography>
              )}
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={resetAndClose} disabled={busy}>
          Close
        </Button>
        {isPending && (
          <>
            <Button
              color="error"
              onClick={() => onReject(note.trim() || undefined)}
              disabled={busy}
              startIcon={rejecting ? <CircularProgress size={16} /> : null}
            >
              {rejecting ? 'Rejecting...' : 'Reject'}
            </Button>
            <Button
              variant="contained"
              onClick={() => onApprove(note.trim() || undefined)}
              disabled={busy}
              startIcon={approving ? <CircularProgress size={16} /> : null}
            >
              {approving ? 'Approving...' : 'Approve'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DocumentReviewDialog;
